/* eslint-disable @typescript-eslint/naming-convention -- FILTER_FORMATTERS is keyed by the backend operator
   enum (IS / IS_ONE_OF / GREATER_THAN …), so the keys are intentionally UPPER_SNAKE — same exemption as
   pages/Explore/.../operators-maps.ts. */
import { formatISO } from '@logz-pkg/utils';
import type { ElasticsearchEnhancedFilterModel, ElasticsearchEnhancedFilterOperator } from '@logz-pkg/models';

// PURE Explore page-context → row formatting (ORIONIQ-1069). No React / no observables — everything that needs
// them is resolved upstream by the consuming shell. The same row builder feeds both the wire preamble and the
// transparency panel, so they can never drift.
//
// Shared by every Explore chat surface (ORIONIQ-1538): this is agent-facing wire wording, so two shells
// describing the same page to the same agent two different ways is the failure this module exists to prevent.
//
// NOTE: the wording below is a PROVISIONAL v1, pending AI-team sign-off — ship it, but expect it to change.

export type ExploreChatView = 'Logs' | 'Metrics' | 'Spans';

// The stable per-field keys the metadata panel checkboxes + the preamble filter operate on (ORIONIQ-1069 R2).
// `attachedLogs`/`attachedExceptions` (ORIONIQ-1084) are the count rows for the logs/exceptions the user attached
// from Explore's "Investigate with AI" buttons — per-item selection is keyed separately (see IAttachedContext).
export type ExploreContextField =
  | 'view'
  | 'accounts'
  | 'timeRange'
  | 'luceneQuery'
  | 'activeFilters'
  | 'spanFilters'
  | 'promql'
  | 'attachedLogs'
  | 'attachedExceptions';

export interface IExploreChatAccount {
  id: string;
  name?: string;
}

// Minimal view of an Explore Spans URL filter (route schema: -spans.ts). Declared here rather than imported
// from a page so this module stays consumable by any shell.
export interface ISpanFilter {
  field: string;
  value: string | number;
  op: 'is' | 'is_not';
}

// The Explore page context attached to a chat message as a preamble (ORIONIQ-1069). Carries serializable,
// already-resolved data so this module stays pure; the metadata panel renders the same fields, so what the user
// sees IS exactly what's attached.
export interface IExploreChatContext {
  view: ExploreChatView;
  // Which app the user is in — SIEM (Security) vs Open 360 (Observability). Both share the same View values
  // (Logs/Metrics/Spans) and Explore route shape, so this is what lets the agent tell the two Explore pages
  // apart. Carried in the preamble intro (never a toggleable metadata row). When absent the identity builder
  // defaults to the Open 360 wording.
  workspace?: 'SIEM' | 'Open360';
  accounts: IExploreChatAccount[];
  from?: string | number;
  to?: string | number;
  // The relative from/to resolved to absolute epoch-ms (ORIONIQ-1069 R3). Set by the consumer ONLY when from/to
  // are relative expressions — resolving `now-*` reads the clock, which a pure util must not do.
  fromResolved?: number;
  toResolved?: number;
  // Logs & spans enhanced filters (free-text LUCENE + field filters); split into the "Lucene query" line and
  // the "Active filters" line.
  query?: ElasticsearchEnhancedFilterModel[];
  // Spans-only URL span filters (field/value/op), rendered as the "Span filters" line.
  spanFilters?: ISpanFilter[];
  // Metrics PromQL expression.
  promql?: string;
}

// One label/value line of the rendered context — shared by the preamble (joined to text) and the panel. `field`
// keys it to the per-field checkbox selection (R2): the panel shows every row + its checkbox; the preamble
// includes only the enabled fields.
export interface IExploreContextRow {
  field: ExploreContextField;
  label: string;
  value: string;
}

// The intro NAMES THE WORKSPACE so the agent distinguishes SIEM (Security) Explore from Open 360
// (Observability) Explore — they share the same View values (Logs/Metrics/Spans) and route shape, so without
// this they're indistinguishable. Ternary (not a lookup map) on `workspace` keeps the object-injection lint
// quiet (same idiom as formatSpanFilters below).
export const buildExplorePageIdentity = (workspace: IExploreChatContext['workspace']): string => {
  const page =
    workspace === 'SIEM' ? 'Logz.io SIEM (Security) Explore page' : 'Logz.io Open 360 (Observability) Explore page';

  return `The user is on the ${page}. Use this context only when it helps answer the question.`;
};

// The label for the accounts row — the metadata panel keys off it to render accounts as chips (not the
// comma-joined preamble string), so it stays in sync with this builder.
export const ACCOUNTS_ROW_LABEL = 'Account(s)';

// Namespaced so ONE session-local disabled-fields set can serve every page without key collisions, and so the
// panel prunes stale opt-outs within this namespace only (ORIONIQ-1384).
export const EXPLORE_CONTEXT_NAMESPACE = 'explore';

export const exploreFieldKey = (field: ExploreContextField): string => `${EXPLORE_CONTEXT_NAMESPACE}.${field}`;

type FilterValue = ElasticsearchEnhancedFilterModel['value'];

const formatList = (value: FilterValue): string => (Array.isArray(value) ? value.join(', ') : String(value));

// Returns the bound as a string, or undefined when it's missing — so a half-specified range renders no bound
// rather than the literal "undefined" (explicit branch, not range[bound], to avoid the object-injection sink).
const getRangeBound = (value: FilterValue, bound: 'gte' | 'lt'): string | undefined => {
  const range = (value ?? {}) as { gte?: unknown; lt?: unknown };
  const raw = bound === 'gte' ? range.gte : range.lt;

  return raw === undefined || raw === null ? undefined : String(raw);
};

// field op value, rendered readably for the agent (and shown verbatim in the panel). Returns null for LUCENE
// (the free-text query is surfaced on its own line, not as an active filter).
const FILTER_FORMATTERS: Record<
  ElasticsearchEnhancedFilterOperator,
  (field: string, value: FilterValue, negate: boolean) => string | null
> = {
  IS: (field, value, negate) => `${field} ${negate ? '!=' : '='} ${formatList(value)}`,
  IS_ONE_OF: (field, value, negate) => `${field} ${negate ? 'is not one of' : 'is one of'} ${formatList(value)}`,
  IS_BETWEEN: (field, value, negate) => {
    const gte = getRangeBound(value, 'gte');
    const lt = getRangeBound(value, 'lt');

    if (gte === undefined || lt === undefined) return null;

    return `${field} ${negate ? 'not between' : 'between'} ${gte} and ${lt}`;
  },
  GREATER_THAN: (field, value, negate) => {
    const gte = getRangeBound(value, 'gte');

    return gte === undefined ? null : `${field} ${negate ? '<=' : '>'} ${gte}`;
  },
  LESS_THAN: (field, value, negate) => {
    const lt = getRangeBound(value, 'lt');

    return lt === undefined ? null : `${field} ${negate ? '>=' : '<'} ${lt}`;
  },
  EXIST: (field, _value, negate) => `${field} ${negate ? 'does not exist' : 'exists'}`,
  LOOKUP: (field, value, negate) => {
    const name = (value as { name?: string } | null)?.name;

    return `${field} ${negate ? 'not in lookup' : 'in lookup'}${name ? ` ${name}` : ''}`;
  },
  LUCENE: () => null,
};

const formatFilter = (filter: ElasticsearchEnhancedFilterModel): string | null => {
  if (filter.isDisabled || filter.invalid) return null;

  if (filter.type === 'LUCENE') return null;

  const field = filter.field?.name;

  if (!field) return null;

  return FILTER_FORMATTERS[filter.type]?.(field, filter.value, filter.negate) ?? null;
};

const splitQuery = (query?: ElasticsearchEnhancedFilterModel[]): { lucene?: string; filters: string[] } => {
  const active = query ?? [];

  const lucene = active
    .filter(filter => filter.type === 'LUCENE' && !filter.isDisabled && typeof filter.value === 'string')
    .map(filter => (filter.value as string).trim())
    .filter(Boolean)
    .join(' AND ');

  const filters = active.map(formatFilter).filter((value): value is string => Boolean(value));

  return { lucene: lucene || undefined, filters };
};

// Exported so the metadata panel's account chips render identically to the wire preamble (no drift).
export const formatAccount = (account: IExploreChatAccount): string =>
  account.name ? `${account.name} (${account.id})` : account.id;

const formatTimeBound = (value: string | number): string => (typeof value === 'number' ? formatISO(value) : value);

// Time range (R3): a relative expression shows the expression AND its resolved absolute window (resolved by the
// consumer — reading the clock is not something a pure util may do); an already-absolute range shows ISO bounds
// only. Resolved PER-BOUND so a mixed relative/absolute range still gets the suffix: a relative bound uses its
// resolved epoch, an already-absolute bound uses itself.
const formatTimeRange = (ctx: IExploreChatContext): string | undefined => {
  const { from, to, fromResolved, toResolved } = ctx;

  if (from === undefined || to === undefined) return undefined;

  const expr = `${formatTimeBound(from)} → ${formatTimeBound(to)}`;

  if (typeof from !== 'string' && typeof to !== 'string') return expr;

  const resolvedFrom = fromResolved ?? (typeof from === 'number' ? from : undefined);
  const resolvedTo = toResolved ?? (typeof to === 'number' ? to : undefined);

  if (resolvedFrom === undefined || resolvedTo === undefined) return expr;

  return `${expr} (resolved ${formatISO(resolvedFrom)} → ${formatISO(resolvedTo)})`;
};

// Ternary (not a lookup map) on `op` keeps the object-injection lint quiet.
const formatSpanFilters = (spanFilters?: ISpanFilter[]): string[] =>
  (spanFilters ?? [])
    .filter(filter => filter.field && filter.value !== null && filter.value !== undefined)
    .map(filter => `${filter.field} ${filter.op === 'is_not' ? '!=' : '='} ${filter.value}`);

// Ordered, non-empty label/value rows describing the current Explore view. Empty fields are omitted entirely.
// Each row carries a stable `field` key so the panel checkboxes + the preamble filter operate per-field (R2).
// Accounts are NOT a row here — they're a per-account section (ORIONIQ-1084), so each consuming shell adds the
// accounts row itself, from its own selection model.
export const buildExploreContextRows = (ctx: IExploreChatContext): IExploreContextRow[] => {
  const rows: IExploreContextRow[] = [{ field: 'view', label: 'View', value: ctx.view }];

  const timeRange = formatTimeRange(ctx);

  if (timeRange) rows.push({ field: 'timeRange', label: 'Time range', value: timeRange });

  const { lucene, filters } = splitQuery(ctx.query);

  if (lucene) rows.push({ field: 'luceneQuery', label: 'Lucene query', value: lucene });

  if (filters.length) rows.push({ field: 'activeFilters', label: 'Active filters', value: filters.join('; ') });

  const spanFilters = formatSpanFilters(ctx.spanFilters);

  if (spanFilters.length) rows.push({ field: 'spanFilters', label: 'Span filters', value: spanFilters.join('; ') });

  const promql = ctx.promql?.trim();

  if (promql) rows.push({ field: 'promql', label: 'PromQL', value: promql });

  return rows;
};
